// Graphiques du cockpit. Couleurs tirées des variables du thème : le mode sombre suit sans recoder.
// Une seule échelle par graphique, traits fins, grille discrète, info-bulle au survol, valeurs aussi
// disponibles en texte (libellés directs ou tableau voisin).

import { cn } from '@cockpit/ui';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MONTHS_SHORT } from '@cockpit/shared';

const v = (name: string) => `rgb(var(--c-${name}))`;
const AXIS = { fontSize: 11, fill: v('ink-3'), fontFamily: 'var(--font-body)' };

function ChartTooltip({
  active,
  payload,
  label,
  fmt,
  labelFmt,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string; dataKey: string }[];
  label?: string | number;
  fmt: (n: number) => string;
  labelFmt?: (l: string | number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-line-soft bg-surface px-3 py-2 text-[12.5px] shadow-od-2">
      <p className="mb-1 font-semibold text-ink">{labelFmt ? labelFmt(label ?? '') : label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-ink-2">
          <span aria-hidden className="h-0.5 w-3 rounded-pill" style={{ background: p.color }} />
          {p.name}
          <span className="tnum ml-auto pl-3 font-semibold text-ink">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

/** Cumul mensuel réel contre budget linéarisé. Le mois sélectionné est repéré. */
export function TrendChart({
  actual,
  budget,
  months,
  selected,
  fmt,
}: {
  actual: (number | null)[];
  budget: number[];
  months: number[];
  selected: number;
  fmt: (n: number) => string;
}) {
  const data = months.map((m, i) => ({
    m,
    reel: i < selected ? actual[i] : null,
    reelApres: i >= selected - 1 ? actual[i] : null,
    budget: budget[i],
  }));
  return (
    <div className="h-[150px]" role="img" aria-label="Cumul réel et budget par mois">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={v('line-soft')} />
          <XAxis
            dataKey="m"
            tickFormatter={(m: number) => MONTHS_SHORT[m - 1] ?? ''}
            tick={AXIS}
            axisLine={false}
            tickLine={false}
          />
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip
            cursor={{ stroke: v('line'), strokeWidth: 1 }}
            content={<ChartTooltip fmt={fmt} labelFmt={(m) => MONTHS_SHORT[Number(m) - 1] ?? ''} />}
          />
          <Line
            name="Budget"
            dataKey="budget"
            stroke={v('gray-600')}
            strokeDasharray="4 4"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            name="Réel"
            dataKey="reel"
            stroke={v('navy-600')}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            name="Réel (après le mois choisi)"
            legendType="none"
            dataKey="reelApres"
            stroke={v('navy-200')}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            tooltipType="none"
          />
          <ReferenceLine x={months[selected - 1]} stroke={v('navy-600')} strokeOpacity={0.25} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** DSO mensuel avec la ligne d'objectif. */
export function DsoChart({ series, target }: { series: { month: number; dso: number }[]; target: number }) {
  return (
    <div className="h-[200px]" role="img" aria-label="Évolution mensuelle du DSO">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 12, right: 16, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} stroke={v('line-soft')} />
          <XAxis
            dataKey="month"
            tickFormatter={(m: number) => MONTHS_SHORT[m - 1] ?? ''}
            tick={AXIS}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={AXIS}
            axisLine={false}
            tickLine={false}
            width={44}
            tickFormatter={(n: number) => `${n} j`}
          />
          <Tooltip
            cursor={{ stroke: v('line'), strokeWidth: 1 }}
            content={
              <ChartTooltip
                fmt={(n) => `${Math.round(n)} j`}
                labelFmt={(m) => MONTHS_SHORT[Number(m) - 1] ?? ''}
              />
            }
          />
          <ReferenceLine
            y={target}
            stroke={v('gray-600')}
            strokeDasharray="4 4"
            label={{
              value: `objectif ${target} j`,
              position: 'insideTopRight',
              fontSize: 11,
              fill: v('ink-3'),
            }}
          />
          <Line
            name="DSO"
            dataKey="dso"
            stroke={v('navy-600')}
            strokeWidth={2}
            isAnimationActive={false}
            dot={{ r: 3, fill: v('navy-600'), stroke: v('surface'), strokeWidth: 2 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface BarItem {
  key: string;
  label: string;
  value: number;
  /** Repère (budget) propre à la ligne. */
  ref?: number;
  good: boolean;
}

/** Barres horizontales par entité, avec repère individuel et ligne de référence commune. */
export function HBars({
  items,
  fmt,
  refLine,
  refLabel,
  refFmt,
}: {
  items: BarItem[];
  fmt: (n: number) => string;
  refLine?: number;
  refLabel?: string;
  refFmt?: (n: number) => string;
}) {
  const vals = [0, ...items.flatMap((i) => [i.value, i.ref ?? 0]), refLine ?? 0];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const pos = (n: number) => ((n - min) / span) * 100;
  const zero = pos(0);
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((it) => {
        const a = pos(Math.min(0, it.value));
        const b = pos(Math.max(0, it.value));
        return (
          <div
            key={it.key}
            className="grid grid-cols-[108px_minmax(0,1fr)_88px] items-center gap-3 text-[13px] sm:grid-cols-[140px_minmax(0,1fr)_96px]"
          >
            <span className="truncate text-ink-2">{it.label}</span>
            <div
              className="relative h-5"
              title={`${it.label} : ${fmt(it.value)}${it.ref !== undefined ? ` (repère ${(refFmt ?? fmt)(it.ref)})` : ''}`}
            >
              <span aria-hidden className="absolute inset-y-0 w-px bg-line" style={{ left: `${zero}%` }} />
              <span
                aria-hidden
                className={cn('absolute top-1 h-3 rounded-sm', it.good ? 'bg-navy-600' : 'bg-danger-600')}
                style={{ left: `${a}%`, width: `${Math.max(0.6, b - a)}%` }}
              />
              {it.ref !== undefined && (
                <span
                  aria-hidden
                  className="absolute -top-0.5 h-6 w-0.5 rounded-pill bg-ink"
                  style={{ left: `calc(${pos(it.ref)}% - 1px)` }}
                />
              )}
              {refLine !== undefined && (
                <span
                  aria-hidden
                  className="absolute inset-y-[-4px] border-l border-dashed border-gray-600"
                  style={{ left: `${pos(refLine)}%` }}
                />
              )}
            </div>
            <span className={cn('tnum text-right font-semibold', it.good ? 'text-ink' : 'text-danger-700')}>
              {fmt(it.value)}
            </span>
          </div>
        );
      })}
      {(refLabel || items.some((i) => i.ref !== undefined)) && (
        <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 pl-[120px] text-[12px] text-ink-3 sm:pl-[152px]">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-3 rounded-sm bg-navy-600" /> conforme au repère
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-3 rounded-sm bg-danger-600" /> en retrait
          </span>
          {items.some((i) => i.ref !== undefined) && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-0.5 rounded-pill bg-ink" /> repère budget
            </span>
          )}
          {refLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 border-l border-dashed border-gray-600" /> {refLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export interface BridgeStep {
  label: string;
  value: number;
  total?: boolean;
}

/** Pont de trésorerie en cascade : solde initial, flux, solde final. */
export function Waterfall({ steps, fmt }: { steps: BridgeStep[]; fmt: (n: number) => string }) {
  let run = 0;
  const bars = steps.map((s) => {
    if (s.total) {
      run = s.value;
      return { ...s, from: 0, to: s.value };
    }
    const from = run;
    run += s.value;
    return { ...s, from, to: run };
  });
  const lo = Math.min(0, ...bars.map((b) => Math.min(b.from, b.to)));
  const hi = Math.max(...bars.map((b) => Math.max(b.from, b.to)));
  const span = hi - lo || 1;
  const y = (n: number) => ((hi - n) / span) * 100;
  return (
    <div>
      <div
        className="relative grid h-[180px] gap-2"
        style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0,1fr))` }}
      >
        <span aria-hidden className="absolute inset-x-0 h-px bg-line" style={{ top: `${y(0)}%` }} />
        {bars.map((b) => {
          const top = y(Math.max(b.from, b.to));
          const h = Math.max(0.8, Math.abs(y(b.from) - y(b.to)));
          const cls = b.total ? 'bg-navy-600' : b.value >= 0 ? 'bg-success-600' : 'bg-danger-600';
          return (
            <div key={b.label} className="relative" title={`${b.label} : ${fmt(b.value)}`}>
              <span
                aria-hidden
                className={cn('absolute inset-x-[32%] rounded-sm', cls)}
                style={{ top: `${top}%`, height: `${h}%` }}
              />
            </div>
          );
        })}
      </div>
      <div
        className="mt-2 grid gap-2"
        style={{ gridTemplateColumns: `repeat(${bars.length}, minmax(0,1fr))` }}
      >
        {bars.map((b) => (
          <div key={b.label} className="min-w-0 text-center">
            <p
              className={cn(
                'tnum text-[13px] font-semibold',
                b.total ? 'text-ink' : b.value >= 0 ? 'text-success-700' : 'text-danger-700',
              )}
            >
              {b.total ? fmt(b.value) : `${b.value > 0 ? '+' : ''}${fmt(b.value)}`}
            </p>
            <p className="mt-0.5 text-[11.5px] leading-tight text-ink-3">{b.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Barres divergentes autour de zéro : décomposition d'un écart (négatif = défavorable). */
export function DivergingBars({
  rows,
  fmt,
}: {
  rows: { label: string; value: number }[];
  fmt: (n: number) => string;
}) {
  const mx = Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const w = (Math.abs(r.value) / mx) * 50;
        return (
          <div
            key={r.label}
            className="grid grid-cols-[120px_minmax(0,1fr)_80px] items-center gap-3 text-[13px]"
          >
            <span className="text-ink-2">{r.label}</span>
            <div className="relative h-3 rounded-sm bg-surface-2" title={`${r.label} : ${fmt(r.value)}`}>
              <span aria-hidden className="absolute inset-y-[-3px] left-1/2 w-px bg-gray-500" />
              <span
                aria-hidden
                className={cn(
                  'absolute inset-y-0 rounded-sm',
                  r.value < 0 ? 'bg-danger-600' : 'bg-success-600',
                )}
                style={r.value < 0 ? { right: '50%', width: `${w}%` } : { left: '50%', width: `${w}%` }}
              />
            </div>
            <span
              className={cn(
                'tnum text-right font-semibold',
                r.value < 0 ? 'text-danger-700' : r.value > 0 ? 'text-success-700' : 'text-ink-3',
              )}
            >
              {fmt(r.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
